import { useState } from 'react';
import { read_cookie } from "sfcookies";
import { hostUrl } from "../../../../utils/url-manager";

/**
 * What `POST /hq/company/domain` answers on success.
 *
 * **There is no token in it, by design.** The customer reads the TXT value from their own
 * Organisations screen and publishes it; HQ is not a second place to read a credential from.
 * The user settled that on 2026-09-10, and `AdminDomainCreator` says the same at the other end.
 */
export type AddedDomain = {
    id: number,
    baseUrl: string,
    status: string,
    /** Unix seconds. The customer has this long before the portal has to issue a new one. */
    tokenExpires: number|null,
    domainsUsed: number,
    domainsAllowed: number,
};

type UseAddCompanyDomain = {
    addingDomain: boolean,
    domainError: string,
    domainSuccess: string,
    addDomain: (companyId: string|number, domain: string) => Promise<AddedDomain|undefined>,
    clearDomainMessages: () => void,
};

/**
 * Adds a domain to a company from HQ.
 *
 * The endpoint owns every rule: what a domain looks like, whether the account has one to spare,
 * and the token the customer will publish. A refusal arrives as a sentence written for the admin,
 * so it is shown as it came rather than re-worded here.
 */
export const useAddCompanyDomain: () => UseAddCompanyDomain = (): UseAddCompanyDomain => {
    const [addingDomain, setAddingDomain] = useState<boolean>(false);
    const [domainError, setDomainError] = useState<string>('');
    const [domainSuccess, setDomainSuccess] = useState<string>('');

    function clearDomainMessages(): void {
        setDomainError('');
        setDomainSuccess('');
    }

    async function addDomain(companyId: string|number, domain: string): Promise<AddedDomain|undefined> {
        clearDomainMessages();
        setAddingDomain(true);
        const token: string = read_cookie('auth');
        const request: Request = new Request(hostUrl + 'hq/company/domain', {
            method: 'POST',
            body: JSON.stringify({ companyId: companyId, domain: domain }),
            headers: new Headers({
                'Content-Type': 'application/json',
                'token': token,
            })
        });

        return fetch(request)
            .then((response: Response): Promise<any> => response.json())
            .then((data: any): AddedDomain|undefined => {
                setAddingDomain(false);
                if (data?.status === 'success') {
                    setDomainSuccess(data?.message ?? 'Domain added.');
                    return data?.data as AddedDomain;
                }
                setDomainError(data?.message ?? 'Could not add that domain.');
                return undefined;
            })
            .catch((error: Error): undefined => {
                setAddingDomain(false);
                setDomainError('Could not add that domain.');
                console.error(error);
                return undefined;
            });
    }

    return { addingDomain, domainError, domainSuccess, addDomain, clearDomainMessages };
}
